export const ORDER_SOURCE_META = {
  pos: {
    label: 'POS',
    className: 'bg-slate-100 text-slate-700',
  },
  website: {
    label: 'Webshop',
    className: 'bg-violet-100 text-violet-700',
  },
  foodora: {
    label: 'Foodora',
    className: 'bg-pink-100 text-pink-700',
  },
  lieferando: {
    label: 'Lieferando',
    className: 'bg-orange-100 text-orange-700',
  },
  wolt: {
    label: 'Wolt',
    className: 'bg-cyan-100 text-cyan-700',
  },
};

export const FULFILLMENT_META = {
  'dine-in': {
    label: 'Tisch',
    className: 'bg-blue-100 text-blue-700',
  },
  takeaway: {
    label: 'Abholung',
    className: 'bg-amber-100 text-amber-700',
  },
  delivery: {
    label: 'Lieferung',
    className: 'bg-emerald-100 text-emerald-700',
  },
};

export const ONLINE_WORKFLOW_META = {
  pending: {
    label: 'Neu',
    className: 'bg-amber-100 text-amber-700',
  },
  accepted: {
    label: 'Angenommen',
    className: 'bg-sky-100 text-sky-700',
  },
  ready: {
    label: 'Bereit',
    className: 'bg-emerald-100 text-emerald-700',
  },
  collected: {
    label: 'Abgeholt',
    className: 'bg-violet-100 text-violet-700',
  },
  dispatched: {
    label: 'Unterwegs',
    className: 'bg-indigo-100 text-indigo-700',
  },
  delivered: {
    label: 'Zugestellt',
    className: 'bg-cyan-100 text-cyan-700',
  },
  completed: {
    label: 'Bezahlt',
    className: 'bg-slate-100 text-slate-700',
  },
  cancelled: {
    label: 'Storno',
    className: 'bg-red-100 text-red-600',
  },
};

const ONLINE_NOTE_PREFIXES = [
  'ONLINE_REQUESTED_AT ',
  'ONLINE_CONFIRMED_AT ',
  'ONLINE_ACCEPT_STATUS ',
  'ONLINE_ACCEPTED ',
];

function splitNoteSegments(notes) {
  return String(notes || '')
    .split('|')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function extractOnlineNoteValue(notes, prefix) {
  const segment = splitNoteSegments(notes).find((entry) => entry.startsWith(prefix));
  return segment ? segment.slice(prefix.length).trim() : '';
}

export function normalizeFulfillmentType(type) {
  if (!type) return 'dine-in';
  if (type === 'pickup') return 'takeaway';
  return type;
}

export function getOrderSourceMeta(source) {
  if (!source) return ORDER_SOURCE_META.pos;
  return ORDER_SOURCE_META[source] || {
    label: source,
    className: 'bg-slate-100 text-slate-700',
  };
}

export function getFulfillmentMeta(type) {
  const normalized = normalizeFulfillmentType(type);
  return FULFILLMENT_META[normalized] || FULFILLMENT_META['dine-in'];
}

export function isOnlineOrder(order) {
  const source = String(order?.order_source || 'pos').toLowerCase();
  return source !== 'pos';
}

export function getOrderDisplayName(order, fallback = 'Ohne Tisch') {
  if (isOnlineOrder(order)) {
    return order?.customer_name || 'Kundenbestellung';
  }
  return order?.table_name || order?.customer_name || fallback;
}

export function getOrderFulfillmentMeta(order) {
  const normalized = normalizeFulfillmentType(order?.fulfillment_type);
  if (isOnlineOrder(order) && normalized === 'dine-in') {
    return FULFILLMENT_META.takeaway;
  }
  return FULFILLMENT_META[normalized] || FULFILLMENT_META['dine-in'];
}

export function isPickupOrder(order) {
  const normalized = normalizeFulfillmentType(order?.fulfillment_type);
  if (isOnlineOrder(order) && normalized === 'dine-in') return true;
  return normalized === 'takeaway';
}

export function isDeliveryOrder(order) {
  return normalizeFulfillmentType(order?.fulfillment_type) === 'delivery';
}

export function getOnlineRequestedAt(order) {
  return order?.requested_fulfillment_at || extractOnlineNoteValue(order?.notes, 'ONLINE_REQUESTED_AT ');
}

export function getOnlineConfirmedAt(order) {
  return order?.confirmed_fulfillment_at || extractOnlineNoteValue(order?.notes, 'ONLINE_CONFIRMED_AT ');
}

export function getOnlineAcceptStatus(order) {
  const explicit = String(order?.online_accept_status || '').trim().toLowerCase();
  if (explicit) return explicit;

  const noteStatus = extractOnlineNoteValue(order?.notes, 'ONLINE_ACCEPT_STATUS ').toLowerCase();
  if (noteStatus) return noteStatus;

  return String(order?.notes || '').includes('ONLINE_ACCEPTED') ? 'accepted' : 'pending';
}

export function hasAcceptedOnlineOrder(order) {
  return !['pending', 'cancelled'].includes(getOnlineWorkflowStatus(order));
}

export function getOnlineWorkflowStatus(order) {
  if (order?.status === 'cancelled') return 'cancelled';
  if (order?.status === 'completed') return 'completed';

  const rawStatus = getOnlineAcceptStatus(order);
  const normalized = {
    new: 'pending',
    received: 'pending',
    pending: 'pending',
    accepted: 'accepted',
    confirmed: 'accepted',
    in_progress: 'accepted',
    preparing: 'accepted',
    ready: 'ready',
    ready_for_pickup: 'ready',
    'ready-for-pickup': 'ready',
    picked_up: 'collected',
    'picked-up': 'collected',
    collected: 'collected',
    dispatched: 'dispatched',
    out_for_delivery: 'dispatched',
    delivered: 'delivered',
    completed: 'completed',
    done: 'completed',
    finished: 'completed',
    cancelled: 'cancelled',
    canceled: 'cancelled',
  }[rawStatus] || rawStatus;

  if (normalized) return normalized;
  return 'pending';
}

export function getOnlineWorkflowBucket(order) {
  const status = getOnlineWorkflowStatus(order);
  if (status === 'cancelled') return 'cancelled';
  if (status === 'pending') return 'pending';
  if (status === 'accepted') return 'accepted';
  if (status === 'ready') return 'ready';
  return 'finished';
}

export function getOnlineWorkflowMeta(order) {
  const status = getOnlineWorkflowStatus(order);
  return ONLINE_WORKFLOW_META[status] || ONLINE_WORKFLOW_META.pending;
}

export function isOrderPaid(order) {
  if (order?.status === 'completed') return true;
  if (order?.payment_method) return true;

  const paymentStatus = String(order?.payment_status || '').trim().toLowerCase();
  return ['paid', 'captured', 'authorized', 'completed'].includes(paymentStatus);
}

export function isOrderUnpaid(order) {
  if (['completed', 'cancelled'].includes(order?.status)) return false;
  return !isOrderPaid(order);
}

export function extractCustomerOrderNotes(notes) {
  return splitNoteSegments(notes)
    .filter((segment) => !segment.startsWith('STORNO '))
    .filter((segment) => !ONLINE_NOTE_PREFIXES.some((prefix) => segment.startsWith(prefix)))
    .join(' | ');
}

export function extractStornoReason(notes) {
  const segments = splitNoteSegments(notes).reverse();

  const stornoSegment = segments.find((segment) => segment.startsWith('STORNO '));
  if (!stornoSegment) return '';

  const parts = stornoSegment.split(':');
  if (parts.length < 2) return stornoSegment;
  return parts.slice(1).join(':').trim();
}
